import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { BUILD_META, VERSION_PATH, readVersion } from './build-version';

/**
 * Telling an open tab that the dashboard has changed underneath it, and reloading it without
 * losing its place.
 *
 * BoardFlight ships many times a day and a playground tab stays open for hours, so the ordinary
 * case is somebody working in a build that production stopped serving that morning — on a bad
 * morning, the build that was broken. A single-page app never fetches its own code again once it
 * has loaded, so nothing about that changes until somebody reloads, and nobody reloads a page that
 * appears to be working. The tab has to be told.
 *
 * ## What happens
 *
 * 1. **The check.** A page knows the build it was loaded from by the `<meta>` the build stamped
 *    into `index.html`, and asks `/version.json` which build is deployed (`build-version.ts` says
 *    what the id is, and why it is the bytes rather than the commit). Once a minute while the tab is
 *    visible, and straight away when somebody comes back to it — the moment that matters for a tab
 *    left in the background overnight.
 * 2. **The notice.** A strip across the top of every screen, the playground's included, until the
 *    page reloads (`kit/UpdateBar.tsx`). It cannot be dismissed: the build this tab is running may
 *    be the one that was broken.
 * 3. **The reload.** Before the page goes, what the screen asked to keep is written to
 *    `sessionStorage` — this tab's, gone with it — together with the scroll position and the build
 *    being left. The next page load reads that back, once.
 * 4. **The proof.** A reload that comes back on the build it left was answered from a cache, so it
 *    goes again, once, under a URL no cache has seen. After that the notice simply comes back on the
 *    next check: it goes away when a page really is on the deployed build and at no other time.
 *
 * ## What "its place" means
 *
 * The URL comes back with the reload itself — path, query and fragment. On top of that, the scroll
 * position, and whatever a screen registers with `useKeptAcrossReload`; the playground keeps Game
 * View and the composer's chips that way, and its sandbox, conversation, layout and draft already
 * outlived any reload. What does not come back is what only ever lived inside the old document: a
 * game's in-memory state in the preview, a dialog that was open, a form half filled in on a page
 * that never kept one.
 *
 * ## Why the playground's turn survives this reload and no other
 *
 * An ordinary reload mid-turn ends the sandbox, because nothing in a browser can tell a reload from
 * a closed tab (`Playground.tsx`). This one can tell — it started it — and it knows the page is
 * coming straight back. `isReloadingForUpdate` is how the playground stands aside and lets the new
 * page rejoin the running turn, exactly as a second tab would.
 */

/** How often a visible tab asks. The answer is a hundred bytes off the edge, and it is the point. */
const CHECK_EVERY_MS = 60_000;
/** The least time between two checks set off by the tab coming back, so a flapping focus costs one. */
const RECHECK_GAP_MS = 10_000;
/** The first check after a load. Not at once: the page has just fetched a build, and boot is busy. */
const FIRST_CHECK_MS = 10_000;

const RELOAD_KEY = 'bf.update.reload';
/** How long a reload's record is believed. A reload takes seconds; anything older is not this one. */
const RECORD_TTL_MS = 60_000;
/** How many more times a reload that came back on the build it left is tried. */
const RETRIES = 1;
/** The query parameter that makes a retry a URL no cache has seen. Stripped before anything renders. */
export const CACHE_BUST_PARAM = 'bf-reload';
/** How long a restored scroll position waits for a page to grow tall enough to hold it. */
const SCROLL_WAIT_MS = 5_000;

/* ------------------------------------------------------------------------------ the state */

export interface UpdateState {
  /** A different build is deployed than the one this page was loaded from. */
  available: boolean;
  /** `reloadForUpdate` has run, and the page is on its way out. */
  reloading: boolean;
  /** A sentence the screen on show wants beside the notice — see `useReloadNote`. */
  note: string | null;
}

let state: UpdateState = { available: false, reloading: false, note: null };
const listeners = new Set<() => void>();

function setState(patch: Partial<UpdateState>): void {
  state = { ...state, ...patch };
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const snapshot = () => state;

export function useUpdate(): UpdateState {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/**
 * A sentence for the notice while `note` is not null — the playground's, while a turn is running.
 *
 * The one thing a screen may add, because the one thing that stops somebody clicking Reload is
 * believing it will cost them something, and only the screen knows what they are afraid of losing.
 */
export function useReloadNote(note: string | null): void {
  useEffect(() => {
    if (note === null) return;
    setState({ note });
    return () => {
      if (state.note === note) setState({ note: null });
    };
  }, [note]);
}

/* ------------------------------------------------------------------------------ the check */

/** The build this page was loaded from, or `null` for one that was never stamped — `vite dev`. */
export function runningBuild(): string | null {
  if (typeof document === 'undefined') return null;
  const content = document.querySelector(`meta[name="${BUILD_META}"]`)?.getAttribute('content');
  return content ? content : null;
}

export interface UpdateWatch {
  /** Ask now. A `return` — the tab coming back — is spaced out; the timer's own checks are not. */
  check(reason: 'timer' | 'return'): Promise<void>;
}

/**
 * The comparison, with its inputs handed in so it can be exercised without a browser.
 *
 * `latest` answers the build that is deployed, or `null` when it cannot tell — offline, a 5xx, or
 * a deployment answering with `index.html` because the file is not there. Not being able to tell is
 * never a new version: a notice raised on a failed request would tell a laptop on a train to reload
 * into nothing. Once one has been found there is nothing left to ask, so it stops.
 */
export function watchForUpdates(options: {
  running: string;
  latest: () => Promise<string | null>;
  onAvailable: () => void;
  now?: () => number;
}): UpdateWatch {
  const now = options.now ?? Date.now;
  let lastAt = Number.NEGATIVE_INFINITY;
  let asking = false;
  let found = false;

  return {
    async check(reason) {
      if (found || asking) return;
      if (reason === 'return' && now() - lastAt < RECHECK_GAP_MS) return;
      asking = true;
      lastAt = now();
      try {
        let deployed: string | null = null;
        try {
          deployed = await options.latest();
        } catch {
          deployed = null;
        }
        if (deployed === null || deployed === options.running) return;
        found = true;
        options.onAvailable();
      } finally {
        asking = false;
      }
    },
  };
}

/** The build `/version.json` names, or `null` when the answer is not one. */
async function deployedBuild(): Promise<string | null> {
  // `no-store`: this request exists to see past every cache between the tab and the edge.
  const response = await fetch(VERSION_PATH, { cache: 'no-store', headers: { accept: 'application/json' } });
  // A deployment without the file answers with `index.html` and a 200 — see `readVersion`.
  if (!response.ok || !(response.headers.get('content-type') ?? '').includes('json')) return null;
  return readVersion(await response.json())?.build ?? null;
}

let started = false;

/** Once, from `main.tsx`. Does nothing on a page that was never stamped with a build. */
export function startUpdateChecks(): void {
  const running = runningBuild();
  if (started || running === null) return;
  started = true;

  const watch = watchForUpdates({
    running,
    latest: deployedBuild,
    onAvailable: () => setState({ available: true }),
  });
  const visible = () => document.visibilityState === 'visible';
  const cameBack = () => {
    if (visible()) void watch.check('return');
  };

  window.setTimeout(() => void watch.check('timer'), FIRST_CHECK_MS);
  // Only while somebody could see the answer. A background tab's timers are throttled to nothing
  // anyway, and coming back is itself a check.
  window.setInterval(() => {
    if (visible()) void watch.check('timer');
  }, CHECK_EVERY_MS);
  document.addEventListener('visibilitychange', cameBack);
  window.addEventListener('focus', cameBack);
  window.addEventListener('online', cameBack);
  // A page put back from the back/forward cache is a page from whenever it was put away.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) cameBack();
  });
}

/* ---------------------------------------------------------------------- what is kept, and how */

const keepers = new Map<string, () => unknown>();
const flushers = new Set<() => void>();

/**
 * Run `flush` just before the page reloads itself for an update.
 *
 * For something a screen already keeps on its own, a little late — the composer's draft is written
 * to `localStorage` on a debounce, and the last few keystrokes before a click are inside it.
 */
export function onUpdateReload(flush: () => void): () => void {
  flushers.add(flush);
  return () => {
    flushers.delete(flush);
  };
}

/** Keep what `read` returns across a reload for an update. Must survive `JSON.stringify`. */
export function keepAcrossReload(key: string, read: () => unknown): () => void {
  keepers.set(key, read);
  return () => {
    if (keepers.get(key) === read) keepers.delete(key);
  };
}

/** `keepAcrossReload` for a value a component holds, read at the moment of the reload. */
export function useKeptAcrossReload(key: string, value: unknown): void {
  const latest = useRef(value);
  latest.current = value;
  useEffect(() => keepAcrossReload(key, () => latest.current), [key]);
}

/** What the last reload for an update kept, until each screen has taken its share. */
let restored = new Map<string, unknown>();

/**
 * What `key` held when this tab last reloaded for an update, if `accept` recognises it.
 *
 * `accept` is not a formality. What is read here was written by the build this page just replaced,
 * which is by definition a different version of this code, so a shape is proven rather than assumed.
 */
export function peekRestored<T>(key: string, accept: (value: unknown) => value is T): T | undefined {
  const value = restored.get(key);
  return value !== undefined && accept(value) ? value : undefined;
}

/**
 * `peekRestored`, for the first render of a component — and then spent.
 *
 * Spent in an effect rather than on read, because React renders a component twice in development
 * and a value taken by the first render would be missing from the second. After the commit it is
 * gone, so leaving the screen and coming back to it later starts fresh rather than replaying a
 * state from a reload an hour ago.
 */
export function useRestored<T>(key: string, accept: (value: unknown) => value is T): T | undefined {
  const [value] = useState(() => peekRestored(key, accept));
  useEffect(() => {
    restored.delete(key);
  }, [key]);
  return value;
}

/* ----------------------------------------------------------------------------- the reload */

export interface ReloadRecord {
  /** The build the reload left, or `null` if the page did not know. */
  from: string | null;
  at: number;
  /** 0 for the click, and one more for every retry. */
  attempt: number;
  /** Path, query and fragment when it left: the scroll position belongs to this and nowhere else. */
  href: string;
  scroll: [number, number];
  kept: Record<string, unknown>;
}

let reloading = false;

/**
 * True once this page has started reloading itself for an update.
 *
 * For the playground's unload handlers, which would otherwise end the sandbox: a reload is
 * indistinguishable from a closed tab to them, and this one is not a closed tab.
 */
export function isReloadingForUpdate(): boolean {
  return reloading;
}

/** The Reload button. */
export function reloadForUpdate(): void {
  if (reloading) return;
  reloading = true;
  setState({ reloading: true });

  for (const flush of flushers) {
    try {
      flush();
    } catch {
      // One screen failing to write something down is not a reason for the rest to lose theirs.
    }
  }
  saveRecord({
    from: runningBuild(),
    at: Date.now(),
    attempt: 0,
    href: currentHref(),
    // Less the strip itself, which pushes everything below it down and will not be there when the
    // page comes back: restored as measured, the page would land a strip's height past its place.
    scroll: [window.scrollX, Math.max(0, window.scrollY - stripOffset())],
    kept: collectKept(),
  });
  leave(() => window.location.reload());
}

/** How far the strip is pushing the page down, from the variable `UpdateBar` publishes. */
function stripOffset(): number {
  try {
    return (
      Number.parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--bf-update-offset')) || 0
    );
  } catch {
    return 0;
  }
}

/**
 * Once, from `main.tsx`, before anything renders. `false` means the page is leaving again at once,
 * and there is nothing worth drawing.
 */
export function resumeAfterUpdate(): boolean {
  if (typeof window === 'undefined') return true;

  // First, and whatever else happens: the parameter was only ever for the cache, and a router that
  // saw it would put it into every link it builds from the current URL.
  const bare = withoutCacheBust(window.location.href);
  if (bare !== null) window.history.replaceState(window.history.state, '', bare);

  const record = takeRecord();
  const decision = resumeFrom(record, runningBuild(), Date.now());
  if (decision === 'none' || record === null) {
    autoScrollRestoration();
    return true;
  }

  if (decision === 'retry') {
    reloading = true;
    saveRecord({ ...record, attempt: record.attempt + 1, at: Date.now() });
    leave(() => window.location.replace(withCacheBust(window.location.href, Date.now())));
    return false;
  }

  restored = new Map(Object.entries(record.kept));
  if (record.href === currentHref()) restoreScroll(record.scroll);
  else autoScrollRestoration();
  return true;
}

/**
 * What a page load does with the record a reload left behind.
 *
 * - `none` — there is no record, or it is not from a reload that just happened.
 * - `retry` — the page came back on the build it left, which means a cache answered the reload.
 * - `restore` — anything else, including a retry that came back stale too: the notice returns on the
 *   next check, and the screen might as well be where it was in the meantime.
 */
export function resumeFrom(
  record: ReloadRecord | null,
  running: string | null,
  now: number,
): 'none' | 'retry' | 'restore' {
  if (record === null || now - record.at > RECORD_TTL_MS || now < record.at) return 'none';
  if (running !== null && record.from === running && record.attempt < RETRIES) return 'retry';
  return 'restore';
}

/**
 * A record, as read back by a build that did not write it — so every field is checked, and a
 * record that is not recognisable is no record at all.
 */
export function parseRecord(raw: string | null): ReloadRecord | null {
  if (!raw) return null;
  let value: Partial<Record<keyof ReloadRecord, unknown>>;
  try {
    value = JSON.parse(raw) as typeof value;
  } catch {
    return null;
  }
  if (typeof value !== 'object' || value === null) return null;
  if (typeof value.at !== 'number' || typeof value.attempt !== 'number') return null;

  const scroll = value.scroll;
  const kept = value.kept;
  return {
    from: typeof value.from === 'string' ? value.from : null,
    at: value.at,
    attempt: value.attempt,
    href: typeof value.href === 'string' ? value.href : '',
    scroll:
      Array.isArray(scroll) &&
      scroll.length === 2 &&
      scroll.every((axis) => typeof axis === 'number' && Number.isFinite(axis))
        ? [scroll[0] as number, scroll[1] as number]
        : [0, 0],
    kept:
      typeof kept === 'object' && kept !== null && !Array.isArray(kept)
        ? (kept as Record<string, unknown>)
        : {},
  };
}

/** `href` with the cache-busting parameter set to `stamp`. */
export function withCacheBust(href: string, stamp: number): string {
  const url = new URL(href);
  url.searchParams.set(CACHE_BUST_PARAM, String(stamp));
  return url.toString();
}

/** The path, query and fragment of `href` without the parameter, or `null` if it was not there. */
export function withoutCacheBust(href: string): string | null {
  const url = new URL(href);
  if (!url.searchParams.has(CACHE_BUST_PARAM)) return null;
  url.searchParams.delete(CACHE_BUST_PARAM);
  return `${url.pathname}${url.search}${url.hash}`;
}

function currentHref(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function collectKept(): Record<string, unknown> {
  const kept: Record<string, unknown> = {};
  for (const [key, read] of keepers) {
    try {
      const value = read();
      // Written down one key at a time, so a screen holding something that cannot be serialised
      // costs that screen its value and nobody else theirs.
      if (value !== undefined) kept[key] = JSON.parse(JSON.stringify(value)) as unknown;
    } catch {
      // As above.
    }
  }
  return kept;
}

function saveRecord(record: ReloadRecord): void {
  try {
    window.sessionStorage.setItem(RELOAD_KEY, JSON.stringify(record));
  } catch {
    // Storage that is full or switched off costs the screen its place, not the update.
  }
}

/** Read and remove, so a reload by hand an hour later is not mistaken for this one. */
function takeRecord(): ReloadRecord | null {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_KEY);
    window.sessionStorage.removeItem(RELOAD_KEY);
    return parseRecord(raw);
  } catch {
    return null;
  }
}

/**
 * Go, with the browser's own scroll restoration switched off for the way back.
 *
 * It would restore the position before a page whose content comes from the API is tall enough to
 * hold it, land at the top, and then be in a fight with `restoreScroll`. It is per history entry,
 * which is why every load puts it back.
 */
function leave(go: () => void): void {
  try {
    window.history.scrollRestoration = 'manual';
  } catch {
    // Not every browser lets this be written; the scroll is the only thing that suffers.
  }
  go();
}

function autoScrollRestoration(): void {
  try {
    window.history.scrollRestoration = 'auto';
  } catch {
    // See `leave`.
  }
}

/**
 * Scroll back to where the reload left, once the page is tall enough to be scrolled there.
 *
 * Waits for height rather than for a moment, because what makes a page tall is its data arriving
 * and there is no event for that. Gives up the instant somebody scrolls, types or clicks — a page
 * that yanks itself somewhere after you have started reading is worse than one that forgot — and
 * after a few seconds, going as far as the page allows.
 */
function restoreScroll([x, y]: [number, number]): void {
  const interruptions = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const;
  let interrupted = false;
  const interrupt = () => {
    interrupted = true;
  };
  const finish = () => {
    for (const type of interruptions) window.removeEventListener(type, interrupt);
    autoScrollRestoration();
  };
  if (x === 0 && y === 0) return finish();

  for (const type of interruptions) window.addEventListener(type, interrupt, { passive: true });
  const giveUpAt = Date.now() + SCROLL_WAIT_MS;
  const step = () => {
    if (interrupted) return finish();
    const room = document.documentElement.scrollHeight - window.innerHeight;
    if (room >= y || Date.now() >= giveUpAt) {
      window.scrollTo(x, Math.max(0, Math.min(y, room)));
      return finish();
    }
    window.requestAnimationFrame(step);
  };
  window.requestAnimationFrame(step);
}
